/* UFORIQ 2026 redesign: header, mega menu, menu drawer, mobile search, delivery strip.
 * Works alongside the Krown "Local" header script (section-header.aio.min.js), which still
 * owns <main-header>, <sidebar-drawer> show/hide and predictive search.
 * Keyboard: mega menu triggers are buttons with aria-expanded; Esc closes and returns focus.
 * The drawer traps Tab focus while open and returns focus to the menu button on close.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktopMq = window.matchMedia('(min-width: 1024px)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function visibleFocusables(root) {
    return $$(FOCUSABLE, root).filter(function (el) {
      return !el.closest('[hidden]') && (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
    });
  }

  /* ---------- Header height -> CSS var (sticky filter bar offset) ---------- */
  function trackHeaderHeight() {
    var header = $('[data-uq-header]');
    if (!header) return;
    var section = header.closest('.shopify-section') || header;
    var set = function () {
      var sticky = getComputedStyle(section).position === 'sticky';
      document.documentElement.style.setProperty('--uq-header-h', (sticky ? section.offsetHeight : 0) + 'px');
    };
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(section);
    else window.addEventListener('resize', set);
  }

  /* ---------- Mega menu ---------- */
  function initMegaMenu() {
    var items = $$('[data-uq-mega-item]');
    if (!items.length) return;
    var openItem = null;
    var hoverTimer = null;

    function setOpen(item, open, opts) {
      var trigger = $('[data-uq-mega-trigger]', item);
      var panel = $('[data-uq-mega-panel]', item);
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      item.classList.toggle('is-open', open);
      if (open) {
        if (openItem && openItem !== item) setOpen(openItem, false);
        openItem = item;
        document.body.classList.add('uq-mega-open');
      } else if (openItem === item) {
        openItem = null;
        document.body.classList.remove('uq-mega-open');
        if (opts && opts.returnFocus) trigger.focus();
      }
    }

    items.forEach(function (item) {
      var trigger = $('[data-uq-mega-trigger]', item);
      trigger.addEventListener('click', function () {
        setOpen(item, trigger.getAttribute('aria-expanded') !== 'true');
      });
      item.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !finePointer.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () { setOpen(item, true); }, openItem ? 0 : 120);
      });
      item.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse' || !finePointer.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () { setOpen(item, false); }, 180);
      });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) setOpen(item, false);
      });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          e.stopPropagation();
          setOpen(item, false, { returnFocus: true });
        }
      });
    });

    // Left/Right arrows move between top-level items.
    var topLevel = $$('.uq-nav__list > li > a, .uq-nav__list > li > button');
    topLevel.forEach(function (el, i) {
      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var next = topLevel[(i + (e.key === 'ArrowRight' ? 1 : topLevel.length - 1)) % topLevel.length];
          next.focus();
        } else if (e.key === 'ArrowDown' && el.hasAttribute('data-uq-mega-trigger')) {
          e.preventDefault();
          var item = el.closest('[data-uq-mega-item]');
          setOpen(item, true);
          var first = $('[data-uq-mega-panel] a', item);
          if (first) first.focus();
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (openItem && !openItem.contains(e.target)) setOpen(openItem, false);
    });
    desktopMq.addEventListener && desktopMq.addEventListener('change', function () {
      if (openItem) setOpen(openItem, false);
    });
  }

  /* ---------- Menu drawer ---------- */
  function initDrawer() {
    var drawer = $('[data-uq-drawer]');
    if (!drawer) return;
    // Stack the drawer above .site-overlay (the sticky header creates its own stacking context).
    if (drawer.parentNode !== document.body) document.body.appendChild(drawer);

    var opener = $('[data-uq-drawer-open]');
    var lastFocus = null;

    function isOpen() { return drawer.classList.contains('sidebar--opened'); }

    new MutationObserver(function () {
      if (isOpen()) {
        if (!drawer.dataset.uqWasOpen) {
          drawer.dataset.uqWasOpen = '1';
          lastFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : opener;
          if (opener) opener.setAttribute('aria-expanded', 'true');
          setTimeout(function () {
            var first = visibleFocusables(drawer)[0];
            if (first) first.focus();
          }, reduceMotion ? 0 : 60);
        }
      } else if (drawer.dataset.uqWasOpen) {
        delete drawer.dataset.uqWasOpen;
        if (opener) opener.setAttribute('aria-expanded', 'false');
        var target = lastFocus || opener;
        lastFocus = null;
        if (target && document.contains(target)) setTimeout(function () { target.focus(); }, 20);
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen()) return;
      var f = visibleFocusables(drawer);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // Accordions
    $$('[data-uq-acc-trigger]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });
    });
  }

  /* ---------- Mobile search toggle ---------- */
  function initMobileSearch() {
    var btn = $('[data-uq-search-toggle]');
    if (!btn) return;
    var row = document.getElementById(btn.getAttribute('aria-controls'));
    if (!row) return;
    function set(open) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      row.hidden = !open;
      if (open) {
        var input = $('input[type="search"]', row);
        if (input) input.focus();
      }
    }
    btn.addEventListener('click', function () { set(row.hidden); });
    row.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { set(false); btn.focus(); }
    });
  }

  /* ---------- Category pills: keep the active pill in view ---------- */
  function initPills() {
    var list = $('[data-uq-pills]');
    if (!list) return;
    var active = $('.uq-pill.is-active', list);
    if (active && list.scrollWidth > list.clientWidth) {
      list.scrollLeft = Math.max(0, active.offsetLeft - 24);
    }
  }

  /* ---------- Delivery strip: highlight the saved area ---------- */
  function initDeliveryStrip() {
    var strips = $$('[data-uq-delivery-strip]');
    if (!strips.length) return;
    function apply(loc) {
      strips.forEach(function (strip) {
        strip.setAttribute('data-uq-current', loc || '');
        $$('[data-uq-area]', strip).forEach(function (card) {
          var on = card.getAttribute('data-uq-area') === loc;
          card.classList.toggle('is-current', on);
          var badge = $('[data-uq-area-badge]', card);
          if (badge) badge.hidden = !on;
        });
      });
    }
    var current = window.UforiqLocation && window.UforiqLocation.get ? window.UforiqLocation.get() : null;
    if (!current && window.UforiqArea) current = window.UforiqArea.get(); // area in effect (2026-11-02)
    if (!current) {
      try { var raw = localStorage.getItem('uforiq.location'); current = raw ? JSON.parse(raw).location : null; } catch (e) { current = null; }
    }
    apply(current);
    window.addEventListener('uforiq:location:changed', function (e) { apply(e.detail && e.detail.location); });
  }

  /* ---------- Buy buttons: a sold-out variant can't be submitted (audit #13) ----------
   * Krown marks an unavailable variant only with a "disabled" CSS class, so the SOLD OUT button
   * still submitted the form. The Liquid now renders the real disabled attribute; this keeps it
   * in sync when Krown switches variants (it toggles the class). */
  function initBuyButtons() {
    var sel = 'product-form button[data-js-product-add-to-cart]';
    function sync(btn) {
      var off = btn.classList.contains('disabled');
      if (btn.disabled !== off) btn.disabled = off;
    }
    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) {
        if (r.type === 'attributes') { if (r.target.matches && r.target.matches(sel)) sync(r.target); return; }
        r.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches(sel)) { sync(n); }
          $$(sel, n).forEach(sync);
        });
      });
    });
    $$(sel).forEach(sync);
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    trackHeaderHeight();
    initMegaMenu();
    initDrawer();
    initMobileSearch();
    initPills();
    initDeliveryStrip();
    initBuyButtons();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
